// ============================================
// Message Input Component
// Text input with file upload and send button
// ============================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from './ChatProvider';
import MentionAutocomplete from './MentionAutocomplete';

/**
 * Message input field with file upload, mentions, and send functionality
 */
const MessageInput = () => {
  const { sendMessage, uploadChatFile, sendTypingIndicator, isConnected, replyingTo, clearReplyingTo, currentChannel } = useChat();

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Mention state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [mentions, setMentions] = useState([]);  // Array of {user_type, user_id, display_name}

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    // Send typing start
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      sendTypingIndicator(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      sendTypingIndicator(false);
    }, 2000);
  }, [sendTypingIndicator]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTypingRef.current) {
        sendTypingIndicator(false);
      }
    };
  }, [sendTypingIndicator]);

  // Handle input change
  const handleChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessage(value);
    handleTyping();

    // Check for @ mention trigger
    // Look backwards from cursor to find @ that starts a mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) {
        // Get the text after @ (the search query)
        const query = textBeforeCursor.substring(atIndex + 1);
        // Only show autocomplete if query doesn't contain spaces (single word)
        if (!query.includes(' ')) {
          setShowMentionAutocomplete(true);
          setMentionQuery(query);
          setMentionStartIndex(atIndex);
          return;
        }
      }
    }

    // Hide autocomplete if no valid @ found
    setShowMentionAutocomplete(false);
    setMentionQuery('');
    setMentionStartIndex(-1);
  };

  // Handle mention selection from autocomplete
  const handleMentionSelect = useCallback((member) => {
    if (mentionStartIndex === -1) return;

    // Build the new message with mention inserted
    const beforeMention = message.substring(0, mentionStartIndex);
    const afterMention = message.substring(textareaRef.current?.selectionStart || message.length);
    const mentionText = `@${member.display_name} `;
    const newMessage = beforeMention + mentionText + afterMention;

    setMessage(newMessage);

    // Add to mentions array (avoid duplicates)
    setMentions(prev => {
      const exists = prev.some(m => m.user_type === member.user_type && m.user_id === member.user_id);
      if (!exists) {
        return [...prev, {
          user_type: member.user_type,
          user_id: member.user_id,
          display_name: member.display_name
        }];
      }
      return prev;
    });

    // Close autocomplete
    setShowMentionAutocomplete(false);
    setMentionQuery('');
    setMentionStartIndex(-1);

    // Focus back on input and set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = beforeMention.length + mentionText.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [message, mentionStartIndex]);

  // Close mention autocomplete
  const closeMentionAutocomplete = useCallback(() => {
    setShowMentionAutocomplete(false);
    setMentionQuery('');
    setMentionStartIndex(-1);
  }, []);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadError(null);

    for (const file of files) {
      // Add to uploading list
      const uploadId = Date.now() + Math.random();
      setUploadingFiles(prev => [...prev, { id: uploadId, name: file.name }]);

      try {
        const result = await uploadChatFile(file);

        // Add to attachments
        setAttachments(prev => [...prev, {
          s3_key: result.s3_key,
          url: result.url,
          filename: result.filename,
          file_type: result.file_type,
          content_type: result.content_type,
          size: result.size
        }]);
      } catch (err) {
        setUploadError(err.message || 'Failed to upload file');
      } finally {
        // Remove from uploading list
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      }
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle send
  const handleSend = async () => {
    const trimmedMessage = message.trim();
    const hasContent = trimmedMessage || attachments.length > 0;
    if (!hasContent || isSending || !isConnected || uploadingFiles.length > 0) return;

    setIsSending(true);

    // Clear typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingIndicator(false);
    }

    try {
      // Determine content type
      let contentType = 'text';
      if (attachments.length > 0 && !trimmedMessage) {
        contentType = attachments[0].file_type === 'image' ? 'image' : 'file';
      } else if (attachments.length > 0) {
        contentType = 'mixed';
      }

      // Filter mentions to only include those still in the message
      const finalMentions = mentions.filter(m =>
        trimmedMessage.includes(`@${m.display_name}`)
      );

      const result = await sendMessage(
        trimmedMessage || (attachments.length === 1 ? attachments[0].filename : `${attachments.length} files`),
        contentType,
        attachments,
        replyingTo?.id || null,
        finalMentions
      );

      if (result) {
        setMessage('');
        setAttachments([]);
        setUploadError(null);
        setMentions([]);  // Clear mentions
        clearReplyingTo();
        // Focus back on input
        textareaRef.current?.focus();
      }
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    // Don't send if mention autocomplete is open (let it handle Enter/Tab)
    if (showMentionAutocomplete) {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Escape') {
        // Let MentionAutocomplete handle these keys
        return;
      }
    }

    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canSend = (message.trim() || attachments.length > 0) && isConnected && !isSending && uploadingFiles.length === 0;

  return (
    <div className="p-3">
      {/* Reply context bar */}
      {replyingTo && (
        <div className="flex items-center justify-between mb-2 px-3 py-2 bg-blue-50 border-l-4 border-blue-500 rounded-r">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium">
              Replying to {replyingTo.sender?.display_name || 'Unknown'}
            </p>
            <p className="text-xs text-gray-600 truncate">
              {replyingTo.content}
            </p>
          </div>
          <button
            onClick={clearReplyingTo}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
            title="Cancel reply"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Attachment previews */}
      {(attachments.length > 0 || uploadingFiles.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
          {/* Uploading files */}
          {uploadingFiles.map(file => (
            <div key={file.id} className="flex items-center gap-2 px-2 py-1 bg-gray-200 rounded text-xs">
              <svg className="animate-spin h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="truncate max-w-[100px]">{file.name}</span>
            </div>
          ))}

          {/* Uploaded attachments */}
          {attachments.map((att, index) => (
            <div key={index} className="relative group">
              {att.file_type === 'image' ? (
                <div className="relative w-16 h-16 rounded overflow-hidden border border-gray-200">
                  <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeAttachment(index)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1 bg-gray-200 rounded text-xs">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="truncate max-w-[100px]">{att.filename}</span>
                  <span className="text-gray-400">{formatFileSize(att.size)}</span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="mb-2 px-2 py-1 bg-red-50 text-red-600 text-xs rounded flex items-center justify-between">
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      <div className="flex items-end space-x-2">
        {/* File upload button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!isConnected || isSending}
          className="
            flex-shrink-0 w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full
            flex items-center justify-center transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          title="Attach file"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          multiple
        />

        {/* Text input */}
        <div className="flex-1 relative">
          {/* Mention Autocomplete */}
          <MentionAutocomplete
            channelId={currentChannel?.id}
            searchQuery={mentionQuery}
            position={{ bottom: '100%', left: 0 }}
            onSelect={handleMentionSelect}
            onClose={closeMentionAutocomplete}
            visible={showMentionAutocomplete}
          />

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? "Type a message... (@ to mention)" : "Connecting..."}
            disabled={!isConnected || isSending}
            rows={1}
            className="
              w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl resize-none
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm placeholder-gray-400
            "
            style={{ maxHeight: '120px' }}
          />

          {/* Character count (optional, shows when near limit) */}
          {message.length > 900 && (
            <span className={`absolute bottom-2 right-2 text-xs ${message.length > 1000 ? 'text-red-500' : 'text-gray-400'}`}>
              {message.length}/1000
            </span>
          )}
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="
            flex-shrink-0 w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full
            flex items-center justify-center transition-colors
            disabled:bg-gray-300 disabled:cursor-not-allowed
          "
          title="Send message"
        >
          {isSending ? (
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>

      {/* Hint text */}
      <p className="text-[10px] text-gray-400 mt-1 px-1">
        Enter to send • Shift+Enter new line • @ to mention • 📎 attach files
      </p>
    </div>
  );
};

export default MessageInput;
